import ElementController from '../base-controllers/ElementController';
import { getData, setData } from '../utils/dom';
import { TABS_CONTROLLER_ID } from '../../constants';

const CURRENT_INDEX_DATA_NAME = 'current-index';
const KEYS = {
  end: 'End',
  home: 'Home',
  left: 'ArrowLeft',
  up: 'ArrowUp',
  right: 'ArrowRight',
  down: 'ArrowDown',
};
// Add or subtract depending on key pressed
const DIRECTION = {
  ArrowLeft: -1,
  ArrowUp: -1,
  ArrowRight: 1,
  ArrowDown: 1,
};

/**
 * Controller for handling tabs. A tab is a hidden section of content
 * activated by a menu.
 *
 * To be used with correct aria tabs handling.
 * https://www.w3.org/TR/wai-aria-practices/#tabpanel
 *
 * Consider using data-js-attr (JSAttr global controller) for setting panel
 * attributes, to make them behave like regular text if JS isn't available.
 *
 * @example
 *
 * <div data-controller="Tabs" data-current-index="1">
 *   <div role="tablist" aria-label="Strange fruits">
 *     {% for fruit in fruits %}
 *        <button
 *          type="button"
 *          role="tab"
 *          id="{{ fruit.title|slugify }}-tab"
 *          aria-controls="{{ fruit.title|slugify }}-panel"
 *          {% if not forloop.first %}tabindex="-1"{% endif %}
 *          aria-selected="{% if forloop.first %}true{% else %}false{% endif %}">
 *            {{ fruit.title }}
 *        </button>
 *     {% endfor %}
 *   </div>
 *   {% for fruit in fruits %}
 *     <div
 *       role="tabpanel"
 *       id="{{ fruit.title|slugify }}-panel"
 *       aria-labelledby="{{ fruit.title|slugify }}-tab"
 *       {% if not forloop.first %}hidden{% endif %}
 *       tabindex="0">
 *         {{ fruit.description }}
 *     </div>
 *   {% endfor %}
 * </div>
 */
export default class Tabs extends ElementController {
  static id = TABS_CONTROLLER_ID;

  /**
   * DOM loaded, run initial setup and bind events.
   */
  init() {
    // Get elements.
    this.tabList = this.selectSingle('[role="tablist"]');
    this.tabs = this.selectAll('[role="tab"]');
    this.panels = this.selectAll('[role="tabpanel"]');
    this.maxTabsIndex = this.tabs.length;

    // Set index on tabs elements.
    this.tabs.forEach((el, i) => {
      setData(el, 'index', i + 1);
    });

    // Bind events.
    // Delegate to handle dynamically added tabs.
    this.on(this.tabList, 'click', '[role="tab"]', this.handleTabClick);
    this.on(this.tabList, 'keydown', '[role="tab"]', this.handleTabKeyDown);
  }

  /**
   * Unbind events when destroyed.
   */
  destroy() {
    this.off(this.tabList, 'click', this.handleTabClick);
    this.off(this.tabList, 'keydown', this.handleTabKeyDown);
  }

  /**
   * Activate a tab.
   *
   * @param {number} index - The index to activate.
   */
  activateTab(index) {
    this.deactivateTabs();
    this.deactivatePanels();

    const tab = this.tabs[index - 1];
    this.currentIndex = index;

    // Activate tab
    tab.removeAttribute('tabindex');
    tab.setAttribute('aria-selected', 'true');
    tab.focus();

    // Remove hidden attribute from tab panel to make it visible
    const panel = this.selectSingle(`#${tab.getAttribute('aria-controls')}`);
    panel.removeAttribute('hidden');
  }

  /**
   * Deactivate all tabs.
   */
  deactivateTabs() {
    this.tabs.forEach((tab) => {
      tab.setAttribute('tabindex', '-1');
      tab.setAttribute('aria-selected', 'false');
    });
  }

  /**
   * Deactivate all panels.
   */
  deactivatePanels() {
    this.panels.forEach((panel) => {
      panel.setAttribute('hidden', '');
    });
  }

  /**
   * Check if arrow press orientation is valid.
   *
   * When a tablist's aria-orientation is set to vertical, only up and down
   * arrow should function. In all other cases only left and right arrow
   * function.
   *
   * @param {string} key - The arrow key name
   * @returns {boolean}
   */
  isValidOrientation(key) {
    const isVertical =
      this.tabList.getAttribute('aria-orientation') === 'vertical';

    if (isVertical) {
      if (key === KEYS.up || key === KEYS.down) {
        return true;
      }
    } else if (key === KEYS.left || key === KEYS.right) {
      return true;
    }

    return false;
  }

  /**
   * Switch tab on arrow key press.
   *
   * Either focus the next, previous, first, or last tab depending on the
   * key pressed.
   *
   * @param {string} key - The arrow key name.
   */
  activateTabByKey(key) {
    if (!this.isValidOrientation(key)) {
      return;
    }

    let index;
    const newIndex = this.currentIndex + DIRECTION[key];
    if (newIndex >= 1 && newIndex <= this.maxTabsIndex) {
      // Activate prev/next tab
      index = newIndex;
    } else if (key === KEYS.left || key === KEYS.up) {
      // Activate last tab
      index = this.maxTabsIndex;
    } else if (key === KEYS.right || key === KEYS.down) {
      // Activate first tab
      index = 1;
    }
    this.activateTab(index);
  }

  /**
   * Get current index from data attribute.
   *
   * @returns {number} The index.
   */
  get currentIndex() {
    return getData(this.root, CURRENT_INDEX_DATA_NAME);
  }

  /**
   * Set current index to data attribute.
   *
   * @param {number} value - The index.
   */
  set currentIndex(value) {
    setData(this.root, CURRENT_INDEX_DATA_NAME, value);
  }

  /**
   * Handle tab click.
   *
   * @param {Event} e - Click event.
   */
  handleTabClick = (e) => {
    this.activateTab(getData(e.target.closest('[role="tab"]'), 'index'));
  };

  /**
   * Handle tab key down.
   *
   * @param {Event} e - Key down event.
   */
  handleTabKeyDown = (e) => {
    switch (e.key) {
      case KEYS.end:
        e.preventDefault();
        // Activate last tab
        this.activateTab(this.maxTabsIndex);
        break;

      case KEYS.home:
        e.preventDefault();
        // Activate first tab
        this.activateTab(1);
        break;

      case KEYS.up:
      case KEYS.down:
      case KEYS.left:
      case KEYS.right:
        e.preventDefault();
        this.activateTabByKey(e.key);
        break;

      default:
        // No default
        break;
    }
  };
}
